import io.ktor.client.call.*
import io.ktor.client.request.*
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.*

@Serializable
data class TourStageRow(
    @SerialName("entry_id") val entryId: String,
    @SerialName("player_name") val playerName: String,
    val handicap: Int,
    @SerialName("round_no") val roundNo: Int? = null,
    val seed: Int? = null, val lane: Int? = null, val position: Int? = null,
    val game1: Int? = null, val game2: Int? = null, val game3: Int? = null,
    val played: Int, @SerialName("raw_total") val rawTotal: Int? = null,
    @SerialName("adjusted_total") val adjustedTotal: Int? = null,
    val complete: Boolean,
    @SerialName("withdrawn_phase") val withdrawnPhase: String? = null,
    val rank: Int? = null,
    @SerialName("tie_key") val tieKey: String? = null,
    @SerialName("tie_pending") val tiePending: Boolean = false,
    @SerialName("tie_order") val tieOrder: Int? = null,
)

@Serializable
data class TourResultRow(
    @SerialName("entry_id") val entryId: String,
    @SerialName("player_name") val playerName: String,
    val handicap: Int, val joker: Boolean,
    @SerialName("qualification_rank") val qualificationRank: Int? = null,
    val rank: Int? = null,
    @SerialName("withdrawn_phase") val withdrawnPhase: String? = null,
    val dnf: Boolean,
    val pending: Boolean = false,
    @SerialName("base_rank") val baseRank: Int,
    @SerialName("participant_count") val participantCount: Int,
    @SerialName("base_points") val basePoints: Int? = null,
    @SerialName("joker_multiplier") val jokerMultiplier: Int = 1,
    @SerialName("qualification_bonus") val qualificationBonus: Int? = null,
    @SerialName("podium_bonus") val podiumBonus: Int? = null,
    val points: Int? = null,
)

@Serializable
data class TourPerformanceAward(val id: String, @SerialName("entry_id") val entryId: String, val label: String,
    val note: String = "", val quantity: Int = 1, val phase: String? = null)

@Serializable
data class TourPublishedResult(
    val version: Int,
    @SerialName("published_at") val publishedAt: String,
    val results: List<TourResultRow>,
    val qualification: List<TourStageRow>, val semifinal: List<TourStageRow>, val final: List<TourStageRow>,
    val awards: List<TourPerformanceAward> = emptyList(),
)

@Serializable
data class TourPlayBundle(
    val phase: String, val revision: Long,
    @SerialName("correction_mode") val correctionMode: Boolean = false,
    val qualification: List<TourStageRow>, val semifinal: List<TourStageRow>, val final: List<TourStageRow>,
    val awards: List<TourPerformanceAward> = emptyList(),
    val preview: List<TourResultRow> = emptyList(),
    val published: TourPublishedResult? = null,
)

internal val TOUR_PHASES = listOf("qualification" to "Kvalifikace", "semifinal" to "Semifinále", "final" to "Finále")
internal fun TourPlayBundle.rows(phase: String) = when (phase) { "semifinal" -> semifinal; "final" -> final; else -> qualification }

object TourPlayApi {
    private val publicResultJson = Json { ignoreUnknownKeys = true }
    internal suspend fun lane(token: String, id: String, revision: Long, phase: String, rows: List<TourLaneScoreInput>) =
        operation(token, id, revision, "tour_save_lane") {
            put("p_phase", phase)
            put("p_rows", JsonArray(rows.map { row -> buildJsonObject {
                put("entry_id", row.entryId); put("premiums", row.premiums)
                put("games", JsonArray(row.games.map { it?.let(::JsonPrimitive) ?: JsonNull }))
            } }))
        }
    suspend fun bundle(token: String?, id: String): TourPlayBundle {
        if (token == null) return publicResultJson.decodeFromString(cachedTourPublicResults("results", id))
        return httpClient.post("$SUPABASE_URL/rest/v1/rpc/tour_play_bundle") {
            tourAuth(token); setBody(buildJsonObject { put("p_id", id) })
        }.body()
    }
    private suspend fun operation(token: String, id: String, revision: Long, name: String, fields: JsonObjectBuilder.() -> Unit = {}) {
        httpClient.post("$SUPABASE_URL/rest/v1/rpc/$name") {
            tourAuth(token); setBody(buildJsonObject { put("p_id", id); put("p_revision", revision); fields() })
        }
    }
    suspend fun games(token: String, id: String, revision: Long, entry: String, phase: String, games: List<Int?>) =
        operation(token, id, revision, "tour_save_games") {
            put("p_entry_id", entry); put("p_phase", phase)
            put("p_games", JsonArray(games.map { it?.let(::JsonPrimitive) ?: JsonNull }))
        }
    suspend fun withdraw(token: String, id: String, revision: Long, entry: String, phase: String?, reason: String) =
        operation(token, id, revision, "tour_set_withdrawal") {
            put("p_entry_id", entry); put("p_phase", phase?.let(::JsonPrimitive) ?: JsonNull); put("p_reason", reason)
        }
    suspend fun tie(token: String, id: String, revision: Long, phase: String, entries: List<String>, reason: String) =
        operation(token, id, revision, "tour_resolve_tie") {
            put("p_phase", phase); put("p_entries", JsonArray(entries.map(::JsonPrimitive))); put("p_reason", reason)
        }
    suspend fun advance(token: String, id: String, revision: Long) = operation(token, id, revision, "tour_confirm_advancement")
    suspend fun publish(token: String, id: String, revision: Long) = operation(token, id, revision, "tour_publish_results")
    suspend fun correction(token: String, id: String, revision: Long, reason: String) = operation(token, id, revision, "tour_open_correction") { put("p_reason", reason) }
    suspend fun award(token: String, id: String, revision: Long, awardId: String, entry: String, label: String, note: String, quantity: Int, phase: String) =
        operation(token, id, revision, "tour_save_award_detail") {
            put("p_award_id", awardId); put("p_entry_id", entry); put("p_label", label); put("p_note", note)
            put("p_quantity", quantity); put("p_phase", phase)
        }
    suspend fun splitAward(token: String, id: String, revision: Long, awardId: String, qualification: Int, semifinal: Int, final: Int) =
        operation(token, id, revision, "tour_split_legacy_award") {
            put("p_award_id", awardId); put("p_qualification", qualification); put("p_semifinal", semifinal); put("p_final", final)
        }
}
